<template>
  <section class="flow-assistant" :class="{ 'flow-assistant-open': open === true }" aria-label="Adi flow assistant">
    <header class="flow-assistant-header">
      <div class="flow-assistant-identity">
        <span class="flow-assistant-avatar">
          <Icon icon="tabler:sparkles" width="19" height="19" />
        </span>
        <div>
          <strong>Adi</strong>
          <small>Flow assistant</small>
        </div>
      </div>
      <button type="button" class="btn btn-sm btn-ghost btn-circle" title="Close Adi" @click="emit('close')">
        <Icon icon="tabler:x" width="17" height="17" />
      </button>
    </header>

    <div ref="messageList" class="flow-assistant-messages">
      <article v-for="message in messages" :key="message.id" class="flow-assistant-message" :class="`flow-assistant-message-${message.role}`">
        <div>{{ message.text }}</div>
        <div v-if="message.files.length > 0" class="flow-assistant-files">
          <span v-for="fileName in message.files" :key="fileName">
            <Icon icon="tabler:paperclip" width="13" height="13" />
            {{ fileName }}
          </span>
        </div>
      </article>
      <div v-if="sending === true" class="flow-assistant-typing">
        <span></span><span></span><span></span>
      </div>
    </div>

    <div v-if="stagedFiles.length > 0" class="flow-assistant-staged">
      <span v-for="(file, index) in stagedFiles" :key="`${file.name}-${file.size}`">
        <Icon icon="tabler:file" width="13" height="13" />
        <b>{{ file.name }}</b>
        <button type="button" title="Remove file" @click="stagedFiles.splice(index, 1)">
          <Icon icon="tabler:x" width="12" height="12" />
        </button>
      </span>
    </div>

    <footer class="flow-assistant-composer">
      <input ref="fileInput" type="file" class="hidden" multiple accept=".pdf,.doc,.docx,.txt,.csv,.json,.md,.png,.jpg,.jpeg,.gif,.bmp,.webp" @change="selectFiles" />
      <textarea
        v-model="question"
        class="textarea textarea-bordered"
        rows="3"
        placeholder="Ask Adi to create or improve the flow..."
        :disabled="assistantDisabled"
        @keydown.enter.exact.prevent="send"
      ></textarea>
      <div class="flow-assistant-actions">
        <button type="button" class="btn btn-sm" :disabled="assistantDisabled" @click="fileInput.click()">
          <Icon icon="tabler:paperclip" width="16" height="16" />
          Attach
        </button>
        <button type="button" class="btn btn-sm" :disabled="canSend === false" @click="send">
          <span v-if="sending === true" class="loading loading-spinner loading-xs"></span>
          <Icon v-else icon="tabler:send" width="16" height="16" />
          Send
        </button>
      </div>
      <small>Adi sees the current working copy and can propose updates.</small>
    </footer>
  </section>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import { hasText } from './flowManagerModel';

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  agentId: {
    type: [String, Number],
    default: ''
  },
  currentFlow: {
    type: Array,
    default: () => []
  },
  selectedChannels: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'apply-flow']);
const { currentLanguage } = useI18n();
const question = ref('');
const messages = ref([]);
const stagedFiles = ref([]);
const sending = ref(false);
const sessionId = ref('');
const messageList = ref(null);
const fileInput = ref(null);
let messageSequence = 0;

const agentIdAvailable = computed(() => true);
const assistantDisabled = computed(() => agentIdAvailable.value === false ? true : sending.value === true);
const canSend = computed(() => {
  if (agentIdAvailable.value === false) {
    return false;
  }
  if (sending.value === true) {
    return false;
  }
  if (question.value.trim().length > 0) {
    return true;
  }
  return stagedFiles.value.length > 0;
});

watch(() => props.open, async isOpen => {
  if (isOpen === true && sessionId.value.length === 0 && agentIdAvailable.value === true) {
    await startSession();
  }
  if (isOpen === true) {
    scrollToBottom();
  }
}, { immediate: true });

function addMessage(role, text, files = []) {
  messageSequence = messageSequence + 1;
  messages.value.push({
    id: `adi-${messageSequence}`,
    role,
    text,
    files
  });
  scrollToBottom();
}

function scrollToBottom() {
  nextTick(() => {
    if (messageList.value !== null) {
      messageList.value.scrollTop = messageList.value.scrollHeight;
    }
  });
}

async function startSession() {
  try {
    const response = await axios.post(`${globalState.apiEndpoint}/api/adi/createSession`, {});
    if (response.data !== null && response.data !== undefined && hasText(response.data.sessionId) === true) {
      sessionId.value = response.data.sessionId;
      addMessage('assistant', 'Tell me what you want the conversation to do. I can create steps, improve routing, or work from an attached document.');
      return;
    }
    addMessage('assistant', 'I could not start a flow session. Close this panel and try again.');
  } catch (error) {
    addMessage('assistant', 'I could not connect right now. Please try again in a moment.');
  }
}

function selectFiles(event) {
  const selected = event.target.files;
  if (selected === null) {
    return;
  }
  if (selected === undefined) {
    return;
  }
  for (let index = 0; index < selected.length; index = index + 1) {
    const file = selected[index];
    const exists = stagedFiles.value.some(existing => existing.name === file.name && existing.size === file.size);
    if (exists === false) {
      stagedFiles.value.push(file);
    }
  }
  event.target.value = '';
}

function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = hasText(reader.result) === true ? reader.result : '';
      const separatorIndex = result.indexOf(',');
      resolve({
        fileName: file.name,
        fileType: file.name.includes('.') === true ? file.name.split('.').pop().toLowerCase() : 'unknown',
        fileData: separatorIndex >= 0 ? result.slice(separatorIndex + 1) : result,
        status: 'new',
        hiddenFromSources: false
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function extractResponse(responseData) {
  const rawAnswer = responseData !== null && responseData !== undefined && hasText(responseData.answer) === true ? responseData.answer : '';
  const directFlowUpdate = responseData !== null && responseData !== undefined && Array.isArray(responseData.flowUpdate) === true ? responseData.flowUpdate : null;
  let answer = rawAnswer;
  let flow = directFlowUpdate;
  const answerMatch = rawAnswer.match(/<answer>([\s\S]*?)<\/answer>/);
  if (answerMatch !== null && answerMatch[1] !== undefined) {
    answer = answerMatch[1].trim();
  }
  const flowMatch = rawAnswer.match(/<flowUpdate>([\s\S]*?)<\/flowUpdate>/);
  if (directFlowUpdate === null && flowMatch !== null && flowMatch[1] !== undefined) {
    try {
      const parsed = JSON.parse(flowMatch[1].trim());
      if (Array.isArray(parsed) === true) {
        flow = parsed;
      }
    } catch (error) {
      answer = `${answer}\n\nI prepared a change, but it could not be applied. Please ask me to try again.`;
    }
  }
  answer = answer.replace(/<flowUpdate>[\s\S]*?<\/flowUpdate>/g, '').trim();
  if (answer.length === 0) {
    answer = flow !== null ? 'I updated the flow.' : 'I could not understand the response. Please try again.';
  }
  return { answer, flow };
}

async function send() {
  if (canSend.value === false) {
    return;
  }
  if (sessionId.value.length === 0) {
    await startSession();
  }
  if (sessionId.value.length === 0) {
    return;
  }
  const text = question.value.trim();
  const files = stagedFiles.value.slice();
  const fileNames = files.map(file => file.name);
  const requestQuestion = text.length > 0 ? text : 'Use the attached files to improve this flow.';
  question.value = '';
  stagedFiles.value = [];
  addMessage('user', requestQuestion, fileNames);
  sending.value = true;
  try {
    const payloadFiles = [];
    for (let index = 0; index < files.length; index = index + 1) {
      payloadFiles.push(await readFile(files[index]));
    }
    const requestBody = {
      sessionId: sessionId.value,
      question: requestQuestion,
      language: currentLanguage.value,
      currentAgentId: String(props.agentId),
      agentSetupDraft: {
        agentMode: 'Flow',
        flow: props.currentFlow,
        selectedChannels: props.selectedChannels
      }
    };
    if (payloadFiles.length > 0) {
      requestBody.attachedFiles = payloadFiles;
    }
    const response = await axios.post(`${globalState.apiEndpoint}/api/adi/query`, requestBody);
    const result = extractResponse(response.data);
    addMessage('assistant', result.answer);
    if (result.flow !== null) {
      emit('apply-flow', result.flow);
    }
  } catch (error) {
    addMessage('assistant', 'Something went wrong while updating the flow. Your current work is safe, so you can try again.');
  } finally {
    sending.value = false;
    scrollToBottom();
  }
}
</script>

<style scoped>
.flow-assistant {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(390px, calc(100% - 1rem));
  display: flex;
  flex-direction: column;
  z-index: 40;
  border-left: 1px solid #cfdbe8;
  background: #ffffff;
  box-shadow: -18px 0 48px rgba(15, 23, 42, 0.17);
  transform: translateX(102%);
  transition: transform 0.2s ease;
}

.flow-assistant-open {
  transform: translateX(0);
}

.flow-assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem;
  border-bottom: 1px solid #dbe5ef;
  background: #f8fafc;
}

.flow-assistant-identity {
  display: flex;
  align-items: center;
}

.flow-assistant-avatar {
  width: 2.25rem;
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #294668;
  border-radius: 0.6rem;
  background: #102d4f;
  color: #ffffff;
}

.flow-assistant-identity > div {
  display: flex;
  flex-direction: column;
  margin-left: 0.6rem;
}

.flow-assistant-identity strong {
  color: #10233e;
  font-size: 0.8rem;
  font-weight: 720;
}

.flow-assistant-identity small {
  margin-top: 0.05rem;
  color: #61718a;
  font-size: 0.62rem;
}

.flow-assistant-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.9rem;
  background: #f3f6fa;
}

.flow-assistant-message {
  max-width: 86%;
  padding: 0.65rem 0.75rem;
  border-radius: 0.55rem;
  white-space: pre-wrap;
  font-size: 0.7rem;
  line-height: 1.5;
}

.flow-assistant-message + .flow-assistant-message {
  margin-top: 0.65rem;
}

.flow-assistant-message-assistant {
  border: 1px solid #d9e3ed;
  border-bottom-left-radius: 0.15rem;
  background: #ffffff;
  color: #344f6d;
}

.flow-assistant-message-user {
  margin-left: auto;
  border-bottom-right-radius: 0.15rem;
  background: #153b65;
  color: #ffffff;
}

.flow-assistant-files {
  margin-top: 0.5rem;
}

.flow-assistant-files span {
  display: flex;
  align-items: center;
  font-size: 0.62rem;
}

.flow-assistant-files svg {
  margin-right: 0.25rem;
}

.flow-assistant-typing {
  display: flex;
  width: fit-content;
  margin-top: 0.65rem;
  padding: 0.7rem;
  border: 1px solid #d9e3ed;
  border-radius: 0.55rem;
  background: #ffffff;
}

.flow-assistant-typing span {
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 999px;
  background: #64748b;
  animation: flow-assistant-pulse 1s infinite alternate;
}

.flow-assistant-typing span + span {
  margin-left: 0.25rem;
}

.flow-assistant-typing span:nth-child(2) {
  animation-delay: 0.15s;
}

.flow-assistant-typing span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes flow-assistant-pulse {
  from { opacity: 0.3; transform: translateY(1px); }
  to { opacity: 1; transform: translateY(-2px); }
}

.flow-assistant-staged {
  padding: 0.55rem 0.7rem 0;
  border-top: 1px solid #dbe5ef;
}

.flow-assistant-staged > span {
  display: flex;
  align-items: center;
  padding: 0.35rem 0.45rem;
  border: 1px solid #dbe6f0;
  border-radius: 0.4rem;
  background: #f1f6fb;
  font-size: 0.62rem;
}

.flow-assistant-staged > span + span {
  margin-top: 0.3rem;
}

.flow-assistant-staged b {
  margin-left: 0.3rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-assistant-staged button {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #64748b;
}

.flow-assistant-composer {
  padding: 0.7rem;
  border-top: 1px solid #dbe5ef;
  background: #ffffff;
}

.flow-assistant-composer .textarea {
  width: 100%;
  resize: none;
  font-size: 0.72rem;
  border-color: #d1dce9;
  border-radius: 0.45rem;
  box-shadow: none;
}

.flow-assistant-composer .textarea:focus {
  border-color: #4c82bf;
  box-shadow: 0 0 0 3px rgba(47, 114, 199, 0.1);
}

.flow-assistant-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
}

.flow-assistant-actions .btn svg,
.flow-assistant-actions .btn .loading {
  margin-right: 0.25rem;
}

.flow-assistant-composer > small {
  display: block;
  margin-top: 0.45rem;
  color: #94a3b8;
  font-size: 0.6rem;
  text-align: center;
}
</style>
